import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	getNextOpenTime,
	RESTAURANT_HOURS,
} from "../../constants/restaurant-hours";
import { Card } from "../ui/card";

interface RestaurantClosedBannerProps {
	nextOpenTime?: Date;
}

export function RestaurantClosedBanner({
	nextOpenTime,
}: RestaurantClosedBannerProps) {
	const { t } = useTranslation();
	const nextOpen = nextOpenTime || getNextOpenTime();

	const formatTime = (date: Date) => {
		return date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	};

	const formatDate = (date: Date) => {
		const today = new Date();
		const tomorrow = new Date(today);
		tomorrow.setDate(tomorrow.getDate() + 1);

		if (date.toDateString() === today.toDateString()) {
			return t("restaurant_closed_banner.today");
		} else if (date.toDateString() === tomorrow.toDateString()) {
			return t("restaurant_closed_banner.tomorrow");
		} else {
			return date.toLocaleDateString("en-US", {
				weekday: "long",
				month: "short",
				day: "numeric",
			});
		}
	};

	return (
		<Card className="border-destructive/20 bg-destructive/5 p-3 sm:p-4">
			<div className="flex items-start gap-2 sm:gap-3">
				<Clock className="mt-0.5 h-4 w-4 sm:h-5 sm:w-5 text-destructive shrink-0" />
				<div className="flex-1 space-y-1.5 sm:space-y-2 min-w-0">
					<h3 className="font-semibold text-sm sm:text-base text-destructive">
						{t("restaurant_closed_banner.title")}
					</h3>

					{nextOpen && (
						<p className="text-xs sm:text-sm text-destructive/80">
							{t("restaurant_closed_banner.reopen_message", {
								date: formatDate(nextOpen),
								time: formatTime(nextOpen),
							})}
						</p>
					)}

					{!nextOpen && (
						<p className="text-xs sm:text-sm text-destructive/80">
							{t("restaurant_closed_banner.temporarily_closed")}
						</p>
					)}

					<div className="text-xs text-destructive/70">
						<div className="sm:hidden space-y-0.5">
							<div>{t("restaurant_closed_banner.hours_label")}</div>
							<div>
								{t("restaurant_closed_banner.weekday_hours_label")}{" "}
								{RESTAURANT_HOURS.weekday.open} -{" "}
								{RESTAURANT_HOURS.weekday.close}
							</div>
							<div>
								{t("restaurant_closed_banner.weekend_hours_label")}{" "}
								{RESTAURANT_HOURS.weekend.open} -{" "}
								{RESTAURANT_HOURS.weekend.close}
							</div>
						</div>
						<div className="hidden sm:flex sm:items-center sm:gap-2">
							<span>{t("restaurant_closed_banner.hours_label")}</span>
							<span>
								{t("restaurant_closed_banner.weekday_hours_label")}{" "}
								{RESTAURANT_HOURS.weekday.open} -{" "}
								{RESTAURANT_HOURS.weekday.close}
							</span>
							<span>•</span>
							<span>
								{t("restaurant_closed_banner.weekend_hours_label")}{" "}
								{RESTAURANT_HOURS.weekend.open} -{" "}
								{RESTAURANT_HOURS.weekend.close}
							</span>
						</div>
					</div>

					<p className="text-xs sm:text-sm text-destructive/80">
						{t("restaurant_closed_banner.order_message")}
					</p>
				</div>
			</div>
		</Card>
	);
}
